import {EventEmitter, Directive, OnInit, OnDestroy, Output} from '@angular/core';
import {Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {Globals} from '../../../core/services/globals';
import {Listing} from '../../../core/models/listing';

@Directive()
export abstract class AbstractListingsSearchComponent implements OnInit, OnDestroy {
  @Output() filterListings = new EventEmitter<object>();

  public showFiltersBln = false;
  public filterHTMLStr = '+ Show Filters';

  public model: Model = {
    searchType: {value: 'Not Specified'},
    area: {value: ''},
    priceRangeMin: {value: ''},
    priceRangeMax: {value: ''},
    bedrooms: {value: 'Any'},
    bathrooms: {value: 'Any'},
    lotSize: {value: 'Any'},
    squareFeet: {value: 'Any'},
    yearBuilt: {value: 'All'},
  };

  public areas: { name: string; value: string }[] = [];

  private subscription!: Subscription;

  constructor(protected router: Router, protected globals: Globals) {
    this.initializeModelValues();
  }

  ngOnInit(): void {
    this.subscription = this.globals.globals$.subscribe((globalsState) => {
      // Sync the model only when the Globals fields differ
      if (this.model.area.value !== globalsState.districtList) {
        this.model.area.value = globalsState.districtList;
      }
      if (this.model.searchType.value !== globalsState.propertyTypeStr) {
        this.model.searchType.value = globalsState.propertyTypeStr;
      }
      if (this.model.priceRangeMin.value !== globalsState.searchMin) {
        this.model.priceRangeMin.value = globalsState.searchMin;
      }
      if (this.model.priceRangeMax.value !== globalsState.searchMax) {
        this.model.priceRangeMax.value = globalsState.searchMax;
      }
      if (this.model.bedrooms.value !== globalsState.bedroomsNum) {
        this.model.bedrooms.value = globalsState.bedroomsNum;
      }
      if (this.model.bathrooms.value !== globalsState.bathroomsNum) {
        this.model.bathrooms.value = globalsState.bathroomsNum;
      }
      if (this.model.squareFeet.value !== globalsState.squareFeetStr) {
        this.model.squareFeet.value = globalsState.squareFeetStr;
      }
    });
    // Set default areas (provided by derived classes)
    this.areas = this.getDefaultAreas();
  }

  ngOnDestroy(): void {
    // Clean up the subscription to prevent memory leaks
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  /**
   * Listener for area dropdown changes. Updates the model and triggers search logic.
   */
  onAreaChange(newValue: string): void {
    this.globals.isInitialLoad = false;
    this.model.area.value = newValue; // Update the model
    //console.log('Updated area value:', this.model.area.value); // Logs the updated selection
    this.search(); // Trigger the search logic to reflect changes
  }

  onDropdownChange(newValue: string, field: string) {
    this.globals.isInitialLoad = false;
    //console.log(`Updated ${field} value:`, newValue); // Logs the updated selection
    this.model[field].value = newValue; // Update the model
    this.search(); // Trigger the search logic to reflect changes
  }

  onSearchTypeChange(newValue: string) {

    this.model.searchType.value = newValue; // Update the model
    //console.log('Updated area value:', this.model.searchType.value); // Logs the updated selection
    this.search(); // Trigger the search logic to reflect changes
  }

  search(): void {
    try {
      this.globals.isInitialLoad = false;
      // Update Globals based on this model
      this.updateGlobalsFromModel();

      // Clear savedSearchIDStr in Globals
      this.globals.savedSearchIDStr = '';

      // Emit search parameters
      const searchParams = this.extractSearchParams();
      this.filterListings.emit(searchParams);
    } catch (error) {
      console.error('Error during search:', error);
    }
  }

  updateFilters(): void {
    this.showFiltersBln = !this.showFiltersBln;
    this.filterHTMLStr = this.showFiltersBln ? '- Hide Filters' : '+ Show Filters';
  }

  gotoDetail(listing: Listing): void {
    const link = ['/detail', listing.id];
    this.router.navigate(link);
  }

  /**
   * Implement this in the derived class to provide default areas for each region.
   */
  protected abstract getDefaultAreas(): { name: string; value: string }[];

  protected initializeModelValues(): void {
    const {propertyTypeStr, districtList, searchMin, searchMax, bedroomsNum, bathroomsNum, squareFeetStr} = this.globals;

    this.setIfNotEmpty(this.model.searchType, propertyTypeStr);
    this.setIfNotEmpty(this.model.area, districtList);
    this.setIfNotEmpty(this.model.priceRangeMin, searchMin);
    this.setIfNotEmpty(this.model.priceRangeMax, searchMax);
    this.setIfNotEmpty(this.model.bedrooms, bedroomsNum);
    this.setIfNotEmpty(this.model.bathrooms, bathroomsNum);
    this.setIfNotEmpty(this.model.squareFeet, squareFeetStr);
  }

  protected setIfNotEmpty(target: { value: string }, source: string): void {
    if (source !== '') {
      target.value = source;
    }
  }

  protected updateGlobalsFromModel(): void {
    //console.log('updateGlobalsFromModel called with area:', this.model.area.value);

    this.globals.propertyTypeStr = this.model.searchType.value; // Search Type
    // Only update district if this isn't the initial load OR if there's an actual value
    if (!this.globals.isInitialLoad) {
      this.globals.districtList = this.model.area.value; // District/Area
    }

    this.globals.searchMin = this.model.priceRangeMin.value; // Minimum Price
    this.globals.searchMax = this.model.priceRangeMax.value; // Maximum Price
    this.globals.bedroomsNum = this.model.bedrooms.value; // Bedrooms
    this.globals.bathroomsNum = this.model.bathrooms.value; // Bathrooms
    this.globals.squareFeetStr = this.model.squareFeet.value; // Square Feet
  }


  protected extractSearchParams(): object {
    return {
      searchType: this.model.searchType.value,
      area: this.model.area.value,
      priceRangeMin: this.model.priceRangeMin.value,
      priceRangeMax: this.model.priceRangeMax.value,
      bedrooms: this.model.bedrooms.value,
      bathrooms: this.model.bathrooms.value,
      lotSize: this.model.lotSize.value,
      squareFeet: this.model.squareFeet.value,
      yearBuilt: this.model.yearBuilt.value,
    };
  }
}

interface Model {
  searchType: { value: string };
  area: { value: string };
  priceRangeMin: { value: string };
  priceRangeMax: { value: string };
  bedrooms: { value: string };
  bathrooms: { value: string };
  lotSize: { value: string };
  squareFeet: { value: string };
  yearBuilt: { value: string };

  [key: string]: { value: string };
}
